// @flow
/** @jsx jsx */

import _ from 'lodash';
import bemify from 'bemify-js';
import React, { Component } from 'react';

import { jsx } from '@emotion/core';
import {
  Captcha,
  FacebookSSOButton,
  GoogleOneTapFramed,
  // $FlowFixMe
  SSOStartUrlParamsContext,
  SignupType,
} from '@zapier/auth';

import AsyncButton from 'app/common/components/AsyncButton';
import AsyncIndicator from 'app/common/components/AsyncIndicator';
import Config from 'app/common/Config';
import FieldError from 'app/login/components/FieldError';
import GoogleSSOButton from 'app/sso/components/GoogleSSOButton';
import InputLabel from 'app/common/components/InputLabel';
import Keydown from 'app/common/components/Keydown';
import Separator from 'app/common/components/Separator';
import TextInput from 'app/common/components/TextInput';
import TrackData from 'app/track/Data';
import {
  EVENT_CAPTCHA_RENDERED,
  EVENT_CAPTCHA_VERIFICATION_FAIL,
} from 'app/login/constants';
import {
  EVENT_ONE_TAP_GOOGLE_IFRAME_CLOSED,
  EVENT_ONE_TAP_GOOGLE_IFRAME_LOAD_FAILED,
} from 'app/sso/constants';
import 'app/login/components/LoginForm.scss';

import type { GoogleOneTapCredential, GoogleOneTapError } from '@zapier/auth';
import type { Node } from 'react';

const bem = bemify('login-form');

const makeTextNode = (content, modifiers = '', props = {}) => {
  const hasError = modifiers.includes('error');

  return !content ? null : (
    <div
      className={bem(`__text ${modifiers}`)}
      role={hasError ? 'alert' : undefined}
    >
      {typeof content === 'function' ? content(props) : content}
    </div>
  );
};

type LoginField = {
  LabelComponent: string,
  autoFocus: boolean,
  classes: string,
  hideUntilInteraction?: boolean,
  label?: string,
  message: string,
  name: string,
  placeholder: string,
  required?: boolean,
  type: string,
  value: string | boolean,
};

type Props = {
  Promo: () => Node,
  Submit: () => Node,
  canSubmit: boolean,
  description: Node,
  fields: LoginField[],
  footer: Node,
  googleSSOEnabled: boolean,
  facebookSSOEnabled: boolean,
  disableGoogleOneTap: boolean,
  hasGoogleOneTapError: boolean,
  shouldAutologin: boolean,
  googleSSOButtonText: string,
  facebookSSOButtonText: string,
  helpfulMessage: Node,
  isInviting: boolean,
  isSubmitting: boolean,
  isCaptchaRequired: boolean,
  message: string,
  messageType: string,
  welcomeMessage: Node,
  next?: string,
  googleOneTapNext?: ?string,
  onChange: (value: boolean | string, name: string) => void,
  onClick: () => void,
  onGoogleOneTapSubmit: (idToken: string, googleOneTapNext?: ?string) => void,
  onResetFormState: () => void,
  onSubmit: (fields: LoginField[], next?: string, value?: any) => void,
  shouldDelayEmailOnboarding: boolean,
  shouldShowLoginForm: boolean,
  shouldShowSubmitButton: boolean,
  shouldTypePassword: boolean,
  submitText: string,
  track: (eventName: string, meta?: any) => void,
  loginHelperButton?: Node,
  selectedApis: string[],
  useCaseSlug?: string,
};

type State = {
  isGoogleOneTapLoading: boolean,
  isGoogleOneTapSubmitting: boolean,
  shouldHideGoogleOneTap: boolean,
  hasTouched: boolean,
};

const Styles = {
  root: {
    padding: 24,
  },
};

const DefaultSubmitButton = (props: Props) => {
  const { onClick } = props;

  return (
    <AsyncButton
      disabled={!props.canSubmit}
      isWorking={props.isSubmitting}
      kind="large block important"
      onClick={onClick}
    >
      {props.submitText}
    </AsyncButton>
  );
};

class LoginForm extends Component<Props, State> {
  state = {
    isGoogleOneTapLoading: true,
    isGoogleOneTapSubmitting: false,
    // If this is a team invite, then we should hide Google One Tap as the
    // email the user is using needs to match the team invite
    shouldHideGoogleOneTap:
      Boolean(this.props.isInviting) || this.props.disableGoogleOneTap,
    hasTouched:
      Boolean(this.props.isInviting) || this.props.disableGoogleOneTap,
  };

  static defaultProps = {
    shouldShowSubmitButton: true,
    Submit: DefaultSubmitButton,
    onResetFormState: () => {},
    isCaptchaRequired: false,
    shouldShowLoginForm: true,
    facebookSSOButtonText: 'Sign Up With Facebook',
  };

  fieldHandlers = {};

  UNSAFE_componentWillMount() {
    this.fieldHandlers = {
      enter: this.onEnterField,
    };
  }

  componentWillUnmount() {
    this.props.onResetFormState();
  }

  onSubmit = () => {
    // Ensure all form fields are revealed
    this.onTouchForm();
    this.props.onSubmit(
      this.props.fields,
      this.props.next,
      this.props.shouldTypePassword
    );
  };

  onEnterField = () => {
    this.onSubmit();
  };

  onCaptchaVerification = (value: string) => {
    this.props.onSubmit(this.props.fields, this.props.next, value);
  };

  onCaptchaExpiration = () => {
    this.props.track(EVENT_CAPTCHA_VERIFICATION_FAIL, {
      reason: 'captcha expired',
    });
  };

  onTouchForm = () => {
    this.setState({
      hasTouched: true,
    });
  };

  onReceiveGoogleCredentials = (credentials: GoogleOneTapCredential) => {
    this.setState({
      isGoogleOneTapSubmitting: true,
    });

    this.props.onGoogleOneTapSubmit(
      credentials.idToken,
      this.props.googleOneTapNext
    );
  };

  onReceiveGoogleError = (error: GoogleOneTapError) => {
    switch (error.type) {
      case 'userCanceled':
        this.props.track(EVENT_ONE_TAP_GOOGLE_IFRAME_CLOSED, {
          error,
        });
        break;
      default:
        this.props.track(EVENT_ONE_TAP_GOOGLE_IFRAME_LOAD_FAILED, {
          error,
        });
        break;
    }

    this.setState({
      shouldHideGoogleOneTap: true,
      isGoogleOneTapLoading: false,
      // If we can't display Google One Tap, forcefully reveal all the
      // form fields
      hasTouched: true,
    });
  };

  onGoogleIframeLoaded = () => {
    this.setState({
      isGoogleOneTapLoading: false,
    });
  };

  isFormCompleted = () => {
    // Some LoginForms have no fields, such as LockedFormContainer
    if (_.isEmpty(this.props.fields)) {
      return true;
    }

    return this.props.fields.every(f => {
      if (!f.required) {
        return true;
      }

      return !_.isEmpty(f.value);
    });
  };

  renderGoogleSSO = () => {
    const { shouldHideGoogleOneTap } = this.state;
    const {
      googleSSOButtonText,
      googleSSOEnabled,
      hasGoogleOneTapError,
      shouldShowLoginForm,
      shouldAutologin,
      shouldDelayEmailOnboarding,
      next,
      useCaseSlug,
    } = this.props;

    // Hide the Google SSO component when there is an error due to a One Tap
    // failure, or if SSO is outright disable.
    if (hasGoogleOneTapError || !googleSSOEnabled) {
      return null;
    }

    // If the user hits the X in the Google One Tap login, we will display the
    // SSO login button. As well, if the Google One Tap encounters an error
    // during load, we will display the SSO button.
    const shouldShowSeparator = googleSSOEnabled && shouldShowLoginForm;

    const queryParams = {
      use_case_slug: useCaseSlug,
      should_delay_email_onboarding: shouldDelayEmailOnboarding,
      next,
    };

    return (
      <React.Fragment>
        {shouldHideGoogleOneTap ? (
          <GoogleSSOButton
            isBlue={true}
            label={googleSSOButtonText}
            queryParams={queryParams}
          />
        ) : (
          <GoogleOneTapFramed
            googleClientId={Config.googleClientId}
            onGoogleAuthenticateError={this.onReceiveGoogleError}
            onGoogleAuthenticateSuccess={this.onReceiveGoogleCredentials}
            onGoogleIframeLoaded={this.onGoogleIframeLoaded}
            shouldAutologin={shouldAutologin}
          />
        )}
        {shouldShowSeparator ? <Separator kind="light">OR</Separator> : null}
      </React.Fragment>
    );
  };

  renderFacebookSSO = () => {
    const {
      facebookSSOButtonText,
      shouldShowLoginForm,
      facebookSSOEnabled,
      selectedApis,
      useCaseSlug,
      shouldDelayEmailOnboarding,
      next,
    } = this.props;

    if (!facebookSSOEnabled) {
      return null;
    }

    const shouldShowSeparator = shouldShowLoginForm;

    const onTrackSignupIntent = () => {
      TrackData.trackSignupIntent(SignupType.facebookSso, {
        selectedApis,
        useCaseSlug,
      });
    };

    const onTrackSignupConversion = async userId => {
      await TrackData.trackSignupConversion(SignupType.facebookSso, {
        selectedApis,
        userId,
        useCaseSlug,
      });
    };

    return (
      <React.Fragment>
        <SSOStartUrlParamsContext.Provider
          value={{
            next,
            should_delay_email_onboarding: shouldDelayEmailOnboarding,
          }}
        >
          <FacebookSSOButton
            label={facebookSSOButtonText}
            onTrackSignupConversion={onTrackSignupConversion}
            onTrackSignupIntent={onTrackSignupIntent}
          />
        </SSOStartUrlParamsContext.Provider>
        {shouldShowSeparator ? <Separator kind="light">OR</Separator> : null}
      </React.Fragment>
    );
  };

  renderLoginForm = (props: Props) => {
    const {
      hasGoogleOneTapError,
      onSubmit,
      shouldShowSubmitButton,
      Submit,
      loginHelperButton,
    } = props;
    if (!props.shouldShowLoginForm) {
      return loginHelperButton;
    }
    const fields = !props.fields
      ? null
      : props.fields
          .filter(f => {
            // This adds the ability for us to hide fields until the form has
            // been touched. If we encounter a One Tap error, ensure the fields
            // are revealed.
            if (this.state.hasTouched || hasGoogleOneTapError) {
              return true;
            }

            return !f.hideUntilInteraction;
          })
          .map(f => {
            const key = f.name;
            if (f.type === 'error') {
              return (
                <FieldError
                  className={bem('__error', f.classes)}
                  key={`${key}--error`}
                  message={f.message}
                />
              );
            }
            const label = !!f.label && f.type !== 'hidden' && (
              <InputLabel htmlFor={f.name} isBlock={true}>
                {f.label}
              </InputLabel>
            );

            return (
              <div className={bem('__field', f.classes)} key={key}>
                {label}
                <TextInput
                  {..._.omit(f, ['classes', 'hideUntilInteraction'])}
                  className={bem('__input', f.classes)}
                  id={label ? f.name : undefined}
                  onChangeValue={props.onChange}
                  onFocus={() => {
                    props.onChange(f.value, f.name);
                    this.onTouchForm();
                  }}
                />
              </div>
            );
          });
    const submitButtonNode = !(onSubmit && shouldShowSubmitButton) ? null : (
      <Submit
        {...props}
        disabled={!this.isFormCompleted()}
        onClick={this.onSubmit}
      />
    );
    const message = makeTextNode(
      props.message,
      `--message --${props.messageType}`
    );
    const helpfulMessage = makeTextNode(
      props.helpfulMessage,
      '--helpfulMessage'
    );
    return (
      <React.Fragment>
        <Keydown handlers={this.fieldHandlers}>
          <div>{fields}</div>
        </Keydown>
        {helpfulMessage}
        {submitButtonNode}
        {message}
      </React.Fragment>
    );
  };

  render() {
    const { props } = this;
    const { Promo, hasGoogleOneTapError } = props;

    if (props.isCaptchaRequired) {
      return (
        <div css={Styles.root}>
          <Captcha
            googleCaptchaSiteKey={Config.googleCaptchaSiteKey}
            onCaptchaExpiration={this.onCaptchaExpiration}
            onCaptchaVerification={this.onCaptchaVerification}
            onMount={() => this.props.track(EVENT_CAPTCHA_RENDERED)}
          />
        </div>
      );
    }

    const description = makeTextNode(props.description, '--description', props);
    const footer = makeTextNode(props.footer, '--footer');
    const maybePromo = Promo && <Promo isCondensed={true} {...props} />;
    const welcomeMessage = makeTextNode(props.welcomeMessage, '--welcome');

    // If the Google SSO is submitting, then we need to hide the form
    if (this.state.isGoogleOneTapSubmitting && !hasGoogleOneTapError) {
      return (
        <div className={bem()}>
          <div className={bem('__submitting-googlesso')}>
            <p>Signing you into Zapier</p>
            <AsyncIndicator />
          </div>
        </div>
      );
    }

    return (
      <div className={bem()}>
        {maybePromo}
        {description}
        {welcomeMessage}
        {this.renderGoogleSSO()}
        {this.renderFacebookSSO()}
        {this.renderLoginForm(props)}
        {footer}
      </div>
    );
  }
}

export default LoginForm;
